import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, type JSX } from 'react';

import type { Blueprint } from '@rta/rta-blueprint-library';
import { RtaBlueprintComponent } from '../app/rta-blueprint-component';
import { createOverheadController } from './readonly-overhead-view';

export type HeadlessOverheadCaptureRendererProps = {
	blueprint: Blueprint;
	width: number;
	height: number;
	assetsBaseUrl?: string;
	/** Called once the off-screen container has finished rendering. Receives the container element so the caller can rasterize it. */
	onReady: (container: HTMLDivElement) => void;
};

const SETTLE_DELAY_MS = 1500;

export const HeadlessOverheadCaptureRenderer = (props: HeadlessOverheadCaptureRendererProps): JSX.Element => {
	const { blueprint, width, height, assetsBaseUrl, onReady } = props;

	const onReadyRef = useRef(onReady);
	onReadyRef.current = onReady;

	const containerRef = useRef<HTMLDivElement | null>(null);

	// Fresh controller per capture so the headless render never shares state with the on-screen designer.
	const controller = useMemo(
		() => createOverheadController(blueprint, { assetsBaseUrl, eventGroup: `headless-overhead-${blueprint.id}-${Date.now()}` }),
		[blueprint, assetsBaseUrl]
	);

	useEffect(() => {
		// Autozoom is asynchronous (GSAP animation). Give the viewport service a fixed window
		// to fit the kitchen + settle, then signal readiness. A fixed delay is simpler and more
		// reliable than racing the viewport event stream for our use case.
		const timer = setTimeout(() => {
			if (containerRef.current) onReadyRef.current(containerRef.current);
		}, SETTLE_DELAY_MS);
		return () => clearTimeout(timer);
	}, [controller]);

	return createPortal(
		<div
			ref={containerRef}
			style={{
				position: 'fixed',
				left: '-99999px',
				top: '0',
				width: `${width}px`,
				height: `${height}px`,
				backgroundColor: '#FFFFFF',
				pointerEvents: 'none'
			}}
		>
			<RtaBlueprintComponent controller={controller} backgroundColor="#FFFFFF" />
		</div>,
		document.body
	) as JSX.Element;
};
